import {StrictMode, useEffect, useMemo} from 'react';
import {IntlProvider} from 'react-intl';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {captureException} from '@sentry/browser';

import {AppConfig, AppProfile} from '@stripe-internal/conversations-assistant';
import {
  MobileAssistant,
  MobileErrorBoundaryFallback,
} from '@stripe-internal/mobile-conversations-assistant';

import {Config} from '../generated/types';
import {
  ObservabilityProvider,
  useObservability,
} from './Providers/ObservabilityProvider';
import {ErrorBoundary} from './components/ErrorBoundary';

function buildConfig(
  config: Config,
  locale = 'en-US',
): {config: AppConfig; profile: AppProfile} {
  const {
    entrypoint: entryPoint,
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    supportPlan,
    livemode,
  } = config;

  const profile: AppProfile = {
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    supportPlan,
    livemode,
  };

  const appConfig: AppConfig = {
    ...config,
    locale,
    entryPoint,
  };

  return {config: appConfig, profile};
}

type Props = {
  locale?: string;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
  config: Config;
};

function AppByEntryPoint({
  config,
  profile,
}: {
  config: AppConfig;
  profile: AppProfile;
}) {
  const observability = useObservability();

  useEffect(() => {
    if (!profile.featureFlags.useExtractedConversationsAssistant) {
      captureException('Running extracted app without feature flag enabled', {
        extra: {
          entryPoint: config.entryPoint,
        },
      });
    }
  }, [config, profile]);

  switch (config.entryPoint) {
    case 'mobile_ios':
    case 'mobile_android':
      return (
        <ErrorBoundary
          fallback={<MobileErrorBoundaryFallback config={config} />}
        >
          <MobileAssistant
            config={config}
            profile={profile}
            observability={observability}
          />
        </ErrorBoundary>
      );
    default:
      captureException('Unsupported extracted app entry point', {
        extra: {
          entryPoint: config.entryPoint,
        },
      });
      return null;
  }
}

export function ExtractedApp({locale = 'en-US', messages, config}: Props) {
  const {config: appConfig, profile} = useMemo(() => {
    return buildConfig(config, locale);
  }, [config, locale]);

  return (
    <IntlProvider locale={locale} messages={messages} onError={() => {}}>
      <StrictMode>
        <ObservabilityProvider>
          <AppByEntryPoint config={appConfig} profile={profile} />
        </ObservabilityProvider>
      </StrictMode>
    </IntlProvider>
  );
}
